/**
 * Chat Input Component
 *
 * Text input with send button for the AI Assistant.
 */

import { type Component, createSignal } from 'solid-js';
import { useAssistant } from './AssistantProvider';

interface ChatInputProps {
  placeholder?: string;
}

export const ChatInput: Component<ChatInputProps> = (props) => {
  const { sendMessage, state } = useAssistant();
  const [input, setInput] = createSignal('');

  const handleSubmit = async (e: Event) => {
    e.preventDefault();
    const message = input().trim();
    if (!message || state().isLoading) return;

    setInput('');
    await sendMessage(message);
  };

  const handleKeyDown = (e: KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSubmit(e);
    }
  };

  return (
    <form onSubmit={handleSubmit} class="flex gap-2 p-4 border-t border-gray-700 bg-gray-800/50">
      <textarea
        value={input()}
        onInput={(e) => setInput(e.currentTarget.value)}
        onKeyDown={handleKeyDown}
        placeholder={props.placeholder || 'Type a message...'}
        disabled={state().isLoading}
        rows={1}
        class="flex-1 bg-gray-700 text-white rounded-lg px-4 py-2 resize-none focus:outline-none focus:ring-2 focus:ring-purple-500 disabled:opacity-50 placeholder-gray-400"
        style={{ 'min-height': '40px', 'max-height': '120px' }}
      />
      <button
        type="submit"
        disabled={!input().trim() || state().isLoading}
        class="px-4 py-2 bg-gradient-to-r from-purple-600 to-pink-600 text-white rounded-lg font-medium hover:opacity-90 disabled:opacity-50 disabled:cursor-not-allowed transition-opacity flex items-center justify-center"
      >
        {state().isLoading ? (
          <svg class="animate-spin w-5 h-5" fill="none" viewBox="0 0 24 24">
            <circle
              class="opacity-25"
              cx="12"
              cy="12"
              r="10"
              stroke="currentColor"
              stroke-width="4"
            />
            <path
              class="opacity-75"
              fill="currentColor"
              d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
            />
          </svg>
        ) : (
          <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path
              stroke-linecap="round"
              stroke-linejoin="round"
              stroke-width="2"
              d="M12 19l9 2-9-18-9 18 9-2zm0 0v-8"
            />
          </svg>
        )}
      </button>
    </form>
  );
};
